"use client";

/**
 * confirmToast — sonner 토스트 기반 확인/취소 액션
 *
 * ConfirmDialog(모달)와 달리 open/onClose state 없이 함수 호출 한 번으로 띄운다 (toast.warning 과 동일한 감각).
 * sonner 의 <Toaster /> 가 이미 앱에 마운트돼 있어야 한다 (Toast 알림 기능과 동일한 전제).
 *
 * 반환값은 토스트 id — 부모 모달이 닫힐 때 같이 닫고 싶으면 toast.dismiss(id) 를 호출한다.
 *
 * @example
 * confirmToast({
 *   title: "제휴사를 삭제하시겠습니까?",
 *   description: "삭제하면 되돌릴 수 없습니다.",
 *   confirmLabel: "삭제",
 *   onConfirm: () => deleteMutation.mutate(...),
 * });
 *
 * @example // 부모 모달이 닫힐 때 같이 닫기
 * const toastId = useRef<string | number | null>(null);
 * toastId.current = confirmToast({ title: "...", onConfirm: handleDelete });
 * // 부모 close 핸들러에서:
 * if (toastId.current != null) toast.dismiss(toastId.current);
 */
import { type ReactNode } from "react";
import { toast } from "sonner";

import { Button } from "../ui/button";

export interface ConfirmToastOptions {
  /** 토스트 제목 */
  title: ReactNode;
  /** 제목 아래 보조 설명 */
  description?: ReactNode;
  /** 확인 버튼 라벨 (기본 "확인") */
  confirmLabel?: string;
  /** 취소 버튼 라벨 (기본 "취소") */
  cancelLabel?: string;
  /** 확인 버튼 클릭 시 실행 */
  onConfirm: () => void;
  /** 취소 버튼 클릭 시 실행 (선택) */
  onCancel?: () => void;
  /** 확인 버튼 색상 (기본 "default" = 프라이머리 색) */
  confirmVariant?: "default" | "destructive";
  /** 자동으로 닫히지 않는 시간(ms). 기본 Infinity(사용자가 버튼을 눌러야 닫힘) */
  duration?: number;
  /** 화면 표시 위치 (기본 "top-center") */
  position?:
    | "top-left"
    | "top-center"
    | "top-right"
    | "bottom-left"
    | "bottom-center"
    | "bottom-right";
}

/** 반환값: 토스트 id (toast.dismiss(id) 로 직접 닫을 때 사용) */
export function confirmToast(options: ConfirmToastOptions): string | number {
  const {
    title,
    description,
    confirmLabel = "확인",
    cancelLabel = "취소",
    onConfirm,
    onCancel,
    confirmVariant = "default",
    duration = Infinity,
    position = "top-center",
  } = options;

  return toast.custom(
    (id) => (
      <div className="w-[min(92vw,380px)] rounded-lg border bg-card p-4 shadow-lg">
        <div className="text-sm font-semibold text-foreground">{title}</div>
        {description && (
          <div className="mt-1 text-sm text-muted-foreground">
            {description}
          </div>
        )}
        <div className="mt-4 flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              toast.dismiss(id);
              onCancel?.();
            }}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={confirmVariant}
            size="sm"
            onClick={() => {
              toast.dismiss(id);
              onConfirm();
            }}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    ),
    { position, duration }
  );
}
